/**
 * Artemis-only sections: the mission phase bar with the current phase, Orion
 * telemetry, the Artemis blog with a "new" badge, NASA images with a lightbox
 * and the aggregated space weather with the crew risk rule
 * (loader-timeline.js, loader-telemetry.js, loader-infopanels.js).
 */
import { useState } from 'react';
import { Images, Rocket, Rss, Sun, X } from 'lucide-react';
import { clockNow, useClock } from '@/state/store';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import {
  currentPhase,
  formatDelay,
  formatDistanceKm,
  formatMet,
  kpClass,
  kpClassLabel,
  mediumImageUrl,
  phaseSegments,
  riskLevel,
} from '../../../awareness/artemis';
import { useAwareness, type NasaImage } from '../../../awareness/store';
import { fmtUtcShort } from '../../format';
import { ErrorLine, Muted, Section } from './Section';
import { hhmmZ, useNow } from './shared';

const TONE = { quiet: 'text-nominal', active: 'text-caution', storm: 'text-warning' } as const;

export function PhaseBar() {
  const timeline = useAwareness((s) => s.artemis.timeline);
  const currentPhaseId = useAwareness((s) => s.artemis.currentPhaseId);
  if (!timeline) return null;
  const segments = phaseSegments(timeline);
  const phase = timeline.phases.find((p) => p.id === currentPhaseId) ?? currentPhase(timeline.phases, clockNow());
  return (
    <div className="border-b border-hairline px-3 py-2 text-xs">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-inset">
        {segments.map((seg) => (
          <div
            key={seg.phase.id}
            title={`${seg.phase.name}: ${seg.phase.description ?? ''}`}
            className={cn('absolute top-0 h-full', seg.phase.id === phase?.id ? 'opacity-100 ring-1 ring-white/70' : 'opacity-60')}
            style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%`, background: seg.phase.color ?? '#8a93a6' }}
          />
        ))}
      </div>
      {phase && (
        <div className="mt-1.5 border-l-2 pl-2 leading-snug" style={{ borderLeftColor: phase.color ?? '#8a93a6' }}>
          <div className="text-ink">{phase.name}</div>
          {phase.commArchitecture?.notes && <div className="text-[10px] text-ink-3">{phase.commArchitecture.notes}</div>}
        </div>
      )}
    </div>
  );
}

function Value({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-control border border-hairline bg-inset/60 px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div className="font-mono text-sm text-ink">{value}</div>
      {sub && <div className="text-[10px] text-ink-3">{sub}</div>}
    </div>
  );
}

export function TelemetrySection() {
  const telemetry = useAwareness((s) => s.artemis.telemetry);
  const lunar = useAwareness((s) => s.lunar);
  const met = telemetry ? formatMet(telemetry.metMs) : null;
  return (
    <Section title="Orion trajectory" icon={Rocket} badge={met ? <span className="font-mono text-[10px] normal-case tracking-normal text-accent">{met}</span> : undefined}>
      {!telemetry ? (
        <Muted>{lunar ? 'The sim clock is outside the ephemeris span.' : 'Waiting for the Orion ephemeris.'}</Muted>
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          <Value label="Dist Earth" value={formatDistanceKm(telemetry.distEarthKm)} />
          <Value label="Dist Moon" value={telemetry.distMoonKm !== null ? formatDistanceKm(telemetry.distMoonKm) : '---'} />
          <Value
            label="Velocity"
            value={telemetry.velocityKmS !== null ? `${telemetry.velocityKmS.toFixed(2)} km/s` : '---'}
            sub={telemetry.velocityKmS !== null ? `${Math.round(telemetry.velocityKmS * 3600).toLocaleString('en')} km/h` : undefined}
          />
          <Value label="Signal delay" value={formatDelay(telemetry.oneWayDelaySec)} sub="one-way" />
          <Value label="Phase" value={telemetry.phaseName ?? '---'} />
          <Value label="Source" value={lunar ? (lunar.source === 'arow' ? 'AROW' : lunar.source === 'horizons' ? 'Horizons' : 'JPL post-flight') : '---'} />
        </div>
      )}
    </Section>
  );
}

export function BlogSection() {
  const blog = useAwareness((s) => s.artemis.blog);
  return (
    <Section
      title="Artemis blog"
      icon={Rss}
      defaultOpen={false}
      badge={blog.newCount > 0 ? <Chip tone="live">{blog.newCount} new</Chip> : undefined}
    >
      {blog.error && <ErrorLine text={`Feed unavailable: ${blog.error}`} />}
      {!blog.data && !blog.error && <Muted>Loading.</Muted>}
      {blog.data && blog.data.length === 0 && <Muted>No posts found</Muted>}
      {blog.data && blog.data.length > 0 && (
        <ul className="flex flex-col gap-2">
          {blog.data.map((post) => {
            const ms = new Date(post.date).getTime();
            const isNew = blog.lastSeenMs !== null && Number.isFinite(ms) && ms > blog.lastSeenMs;
            return (
              <li key={post.link} className={cn('leading-snug', isNew && 'border-l-2 border-live pl-2')}>
                <a href={post.link} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-accent">
                  {post.title}
                </a>
                <div className="text-[10px] text-ink-3">{Number.isFinite(ms) ? fmtUtcShort(ms) : post.date}</div>
                <div className="text-ink-2">{post.excerpt}</div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

export function ImagesSection() {
  const images = useAwareness((s) => s.artemis.images);
  const [lightbox, setLightbox] = useState<NasaImage | null>(null);
  return (
    <Section title="NASA images" icon={Images} defaultOpen={false}>
      {images.error && <ErrorLine text={`Images unavailable: ${images.error}`} />}
      {!images.data && !images.error && <Muted>Loading.</Muted>}
      {images.data && images.data.length === 0 && <Muted>No images found</Muted>}
      {images.data && images.data.length > 0 && (
        <div className="grid grid-cols-3 gap-1">
          {images.data.map((img) => (
            <button
              key={img.thumb}
              type="button"
              title={img.title}
              onClick={() => setLightbox(img)}
              className="aspect-square overflow-hidden rounded-control border border-hairline bg-inset"
            >
              <img src={img.thumb} alt={img.title} loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" aria-label={lightbox.title}>
          <div className="relative max-h-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img src={mediumImageUrl(lightbox.thumb)} alt={lightbox.title} className="max-h-[80vh] rounded-panel" />
            <div className="mt-2 text-center text-xs text-ink-2">{lightbox.title}</div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setLightbox(null)}
              className="absolute -right-2 -top-2 rounded-full bg-raised p-1 text-ink hover:text-accent"
            >
              <X aria-hidden className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-ink-3">{label}</span>
      <span className="text-right text-ink">{children}</span>
    </div>
  );
}

export function ArtemisSpaceWeatherSection() {
  const sw = useAwareness((s) => s.artemis.spaceWeather);
  useNow(60_000);
  const data = sw.data;
  const kp = data?.kpIndex?.current ?? null;
  const cls = kpClass(kp);
  const risk = data ? riskLevel(data) : null;
  const riskTone = risk === 'Elevated' ? TONE.storm : risk === 'Moderate' ? TONE.active : TONE.quiet;
  return (
    <Section
      title="Space weather"
      icon={Sun}
      defaultOpen={false}
      badge={data ? <span className={cn('h-2 w-2 rounded-full bg-current', TONE[cls])} title={kpClassLabel(cls)} /> : undefined}
    >
      {sw.error && <ErrorLine text={`Weather data unavailable: ${sw.error}`} />}
      {!data && !sw.error && <Muted>Loading.</Muted>}
      {data && (
        <div>
          <Row label="Kp index">
            <span className={cn('font-mono', TONE[cls])}>{kp ?? '--'}</span> {kpClassLabel(cls)}
          </Row>
          {data.solarWind?.speed ? <Row label="Solar wind">{Math.round(data.solarWind.speed)} km/s</Row> : null}
          {data.solarWind?.bt ? <Row label="IMF Bt">{Number(data.solarWind.bt).toFixed(1)} nT</Row> : null}
          <Row label="Flares (24 h)">
            {data.solarFlares.length === 0 ? (
              <span className={TONE.quiet}>None</span>
            ) : (
              data.solarFlares.map((f, i) => {
                const c = f.classType || '?';
                const tone = c.charAt(0) === 'X' ? TONE.storm : c.charAt(0) === 'M' ? TONE.active : TONE.quiet;
                return (
                  <span key={`${c}-${i}`} className={tone}>
                    {i > 0 ? ', ' : ''}
                    {c}
                  </span>
                );
              })
            )}
          </Row>
          <Row label="CME alerts">
            <span className={data.cmeAlerts.length > 0 ? TONE.active : TONE.quiet}>
              {data.cmeAlerts.length > 0 ? `${data.cmeAlerts.length} active` : 'None'}
            </span>
          </Row>
          <Row label="Radiation">
            <span className={data.radiationLevel === 'high' ? TONE.storm : data.radiationLevel === 'elevated' ? TONE.active : TONE.quiet}>
              {data.radiationLevel.charAt(0).toUpperCase() + data.radiationLevel.slice(1)}
            </span>
          </Row>
          <div className="my-1 border-t border-hairline" />
          <Row label="Crew exposure">Outside magnetosphere</Row>
          <Row label="Risk level">
            <span className={riskTone}>{risk}</span>
          </Row>
          {data.timestamp && <div className="mt-1 text-[10px] text-ink-3">Updated {hhmmZ(new Date(data.timestamp).getTime())}, refreshes every 10 min</div>}
        </div>
      )}
    </Section>
  );
}

export function ArtemisPanels() {
  const replay = useClock(s => s.playbackRange);
  return (
    <>
      {replay && <p className="p-3 text-[10px] text-ink-3">Historical trajectory replay. Communications architecture is illustrative; these are not recorded antenna contacts. Blog and images are reference material, not synchronized flight telemetry.</p>}
      <PhaseBar />
      <TelemetrySection />
      <BlogSection />
      <ImagesSection />
      {!replay && <ArtemisSpaceWeatherSection />}
    </>
  );
}
